import { GENERIC_DISPLAY_DECIMALS } from '@ui/constants/numbersDecimals';
import { Value } from '@ui/features/Trade/types';
import { formatNumber } from '@ui/lib/bignumber';
import { Nullable } from '@ui/types/utility';
import { MaxRemovableLimitReason } from '@ui/utils/trade/getMaxRemovableCollateral';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { TFunction } from 'next-i18next';

type WithdrawCollateralError =
  | 'below_minimum_input_0'
  | 'exceeds_collateral'
  | 'exceeds_max_removable_leverage'
  | 'exceeds_max_removable_liquidation'
  | 'exceeds_max_removable_take_profit'
  | 'exceed_max_leverage_after_removal'
  | 'below_degen_mode_min_leverage'
  | 'liquidatable_after_removal'
  | 'no_removable_margin';

export const validateWithdrawCollateralAmount = ({
  input,
  maxRemovable,
  maxRemovableLimitReason,
  collateral,
  minLeverage,
  nextLeverage,
  maxLeverage,
  isDegen,
}: {
  input: Value;
  maxRemovable: BigNumber;
  maxRemovableLimitReason: MaxRemovableLimitReason | null;
  collateral: BigNumber;
  minLeverage: number;
  nextLeverage: Nullable<BigNumber>;
  maxLeverage: number;
  isDegen: boolean;
}): {
  error?: WithdrawCollateralError;
} => {
  if (isNil(input.usd) || isNil(input.token)) {
    return {};
  }

  if (input.token.lte(0) || input.usd.lte(0)) {
    return {
      error: 'below_minimum_input_0',
    };
  }

  if (input.token.gte(collateral)) {
    return {
      error: 'exceeds_collateral',
    };
  }

  if (input.token.gt(maxRemovable)) {
    if (maxRemovableLimitReason === 'liquidation') {
      return { error: 'exceeds_max_removable_liquidation' };
    }
    if (maxRemovableLimitReason === 'takeProfit') {
      return { error: 'exceeds_max_removable_take_profit' };
    }

    return { error: 'exceeds_max_removable_leverage' };
  }

  if (!isNil(nextLeverage)) {
    if (nextLeverage.gt(maxLeverage)) {
      return {
        error: 'exceed_max_leverage_after_removal',
      };
    }

    if (isDegen && nextLeverage.lte(minLeverage)) {
      return {
        error: 'below_degen_mode_min_leverage',
      };
    }
  }

  return {};
};

const formatRemovableAmount = (amount: BigNumber, symbol?: string) => {
  const formatted = formatNumber(amount, {
    displayDecimals: GENERIC_DISPLAY_DECIMALS,
    saveDecimalsMode: true,
  });

  return symbol ? `${formatted} ${symbol}` : formatted;
};

export const getWithdrawCollateralAmountErrorMessage = ({
  t,
  error,
  maxLeverage,
  minLeverage,
  maxRemovable,
  leverageAfterMaxRemoval,
  symbol,
}: {
  t: TFunction;
  error?: WithdrawCollateralError;
  maxLeverage: number;
  minLeverage: number;
  maxRemovable: BigNumber;
  leverageAfterMaxRemoval?: BigNumber | null;
  symbol?: string;
}) => {
  if (!error) {
    return '';
  }

  const maxRemovableLabel = formatRemovableAmount(maxRemovable, symbol);
  const leverageAtMaxLabel = leverageAfterMaxRemoval
    ? leverageAfterMaxRemoval.toFixed(1)
    : undefined;

  switch (error) {
    case 'below_minimum_input_0':
      return t('common.Collateral amount must be greater than 0');
    case 'exceeds_collateral':
      return t('common.Amount exceeds position collateral');
    case 'exceeds_max_removable_leverage':
      return t('common.Max {amount} ({maxLeverage}x cap)', {
        amount: maxRemovableLabel,
        maxLeverage,
      });
    case 'exceeds_max_removable_liquidation':
      return leverageAtMaxLabel
        ? t('common.Max {amount} (~{leverage}x, liquidation risk)', {
            amount: maxRemovableLabel,
            leverage: leverageAtMaxLabel,
          })
        : t('common.Max {amount} (liquidation risk)', {
            amount: maxRemovableLabel,
          });
    case 'exceeds_max_removable_take_profit':
      return t('common.Max {amount} (TP cap)', {
        amount: maxRemovableLabel,
      });
    case 'exceed_max_leverage_after_removal':
      return t('common.Exceeds max leverage {maxLeverage}x after removal', {
        maxLeverage,
      });
    case 'below_degen_mode_min_leverage':
      return t('common.Below degen mode min leverage {minLeverage}x', {
        minLeverage,
      });
    case 'liquidatable_after_removal':
      return leverageAtMaxLabel
        ? t('common.~{leverage}x risks liquidation. Max {amount}.', {
            leverage: leverageAtMaxLabel,
            amount: maxRemovableLabel,
          })
        : t('common.Liquidation risk. Max {amount}.', {
            amount: maxRemovableLabel,
          });
    case 'no_removable_margin':
      return t(
        'common.No margin can be removed at current leverage and market price',
      );
    default:
      throw new Error('should not have default case');
  }
};
